import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormLabel,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog,
} from '@mui/material';
import { Person as PersonIcon, Edit as EditIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { IoPrintOutline } from 'react-icons/io5';
import { useDispatch } from 'react-redux';
import { downloadStudentMarksReport } from '../../features/marks/marksSlice';
import Spinner from '../UIElements/Spinner';
import { GoPerson } from 'react-icons/go';

const MarksEntryForm = ({
  selectedStudent,
  onMarksSubmit,
  examType,
  department,
  class: className,
  section,
  instituteType,
  isSubmitting: parentIsSubmitting = false,
  grades,
}) => {
  const dispatch = useDispatch();
  const [initialSubjects, setInitialSubjects] = useState([]);
  const [totalMarks, setTotalMarks] = useState(400);
  const [subjects, setSubjects] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [hasChange, setHasChange] = useState(false);
  const [isMarksAlreadySubmitted, setIsMarksAlreadySubmitted] = useState(false);
  const [modalAlert, setModalAlert] = useState(false);
  //const [isAllMarksEntered,SetIsAllMarksEntered]=useState(false)
  useEffect(() => {
    if (selectedStudent?.marks) {
      const selectedStudentDetails = selectedStudent.marks.map(
        (subject, index) => ({
          id: subject?.scheduleId || index,
          name: subject?.subject,
          maxMarks: subject?.maxMarks || 100,
          marks: subject?.marks,
          status: subject?.status,
          absent: subject?.status?.toLowerCase() === 'absent',
          grade: getGrade(subject?.marks, subject?.maxMarks),
          passMarks: subject?.passMark || 35,
        }),
      );
      setSubjects(selectedStudentDetails);
      setInitialSubjects(selectedStudentDetails);
      setIsMarksAlreadySubmitted(
        selectedStudentDetails.every(
          (subject) => subject.marks === 0 || subject.marks >= 1,
        ),
      );
      setHasChange(false);
    } else {
      setSubjects([]);
    }
  }, [selectedStudent]);
  // Calculate statistics
  const calculateStats = () => {
    const obtainedTotal = subjects?.reduce((sum, subject) => {
      if (subject.absent) return sum;
      return sum + (parseFloat(subject.marks) || 0);
    }, 0);

    const percentage =
      totalMarks > 0 ? ((obtainedTotal / totalMarks) * 100).toFixed(1) : 0;
    const result = subjects?.some((subject) => {
      if (subject.absent) return true; // Absent means fail
      const marks = parseFloat(subject.marks) || 0;
      return marks < subject.passMarks; // Check if marks are below pass marks
    })
      ? 'FAIL'
      : 'PASS';

    return {
      totalMarks,
      marks: obtainedTotal,
      percentage: parseFloat(percentage),
      result,
    };
  };
  const handleMarksChange = (scheduleId, value) => {
    const updatedSubjects = subjects.map((subject) =>
      subject.id === scheduleId
        ? {
            ...subject,
            marks: value,
            status: getStatus(value, subject?.passMarks),
            grade: getGrade(
              parseFloat(value) || 0,
              value >= subject.passMarks ? subject.maxMarks : 0,
            ),
          }
        : subject,
    );
    setSubjects(updatedSubjects);
    const initialStr = JSON.stringify(initialSubjects);
    const currentStr = JSON.stringify(updatedSubjects);
    setHasChange(initialStr !== currentStr);
  };

  const handleAbsentChange = (scheduleId, isAbsent) => {
    if (isAbsent) {
      const updatedSubjects = subjects.map((subject) =>
        subject.id === scheduleId
          ? {
              ...subject,
              status: 'Absent',
              absent: isAbsent,
              marks: isAbsent ? 0 : '',
              grade: getGrade(0, subject?.maxMarks),
            }
          : subject,
      );
      setSubjects(updatedSubjects);
      const initialStr = JSON.stringify(initialSubjects);
      const currentStr = JSON.stringify(updatedSubjects);
      setHasChange(initialStr !== currentStr);
    } else {
      const pastMarks = initialSubjects.find((obj) => obj.id === scheduleId);
      const updatedSubjects = subjects.map((subject) =>
        subject.id === scheduleId
          ? {
              ...subject,
              absent: isAbsent,
              marks: pastMarks.marks,
              grade: pastMarks.grade,
              status: pastMarks.status,
            }
          : subject,
      );
      setSubjects(updatedSubjects);
      console.log('updated', updatedSubjects);
      const initialStr = JSON.stringify(initialSubjects);
      const currentStr = JSON.stringify(updatedSubjects);
      setHasChange(initialStr !== currentStr);
    }
  };
  const getStatus = (marks, passMarks) => {
    if (marks === null) {
      return '--';
    } else if (marks >= passMarks) {
      return 'Pass';
    } else return 'Fail';
  };
  const getGrade = (marks, maxMarks) => {
    let actualPercentage;
    if (marks === null) {
      return null;
    }
    if (maxMarks === 0) {
      actualPercentage = 0;
    } else {
      actualPercentage = (marks / maxMarks) * 100;
    }
    const displayedPercentage = Math.floor(actualPercentage);
    for (const grade of grades) {
      if (
        displayedPercentage >= grade.rangeFrom &&
        displayedPercentage <= grade.rangeTo
      ) {
        return grade.grade;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    // this return  true if any subject contains marks as null or empty
    const isAllMarksNotEntered = subjects.some(
      (obj) => obj.marks === null || obj.marks === '',
    );
    if (isAllMarksNotEntered) {
      setModalAlert(true);
    } else {
      const isCurrentlySubmitting = isSubmitting || parentIsSubmitting;
      if (selectedStudent && onMarksSubmit && !isCurrentlySubmitting) {
        setIsSubmitting(true);
        try {
          const payload = subjects.map((subject) => ({
            scheduleId: subject.id,
            subject: subject.name,
            passMark: subject.passMarks,
            maxMarks: subject.maxMarks,
            status: subject?.status,
            marks: subject.marks,
            grade: subject?.grade,
          }));
          await onMarksSubmit(selectedStudent, payload);
        } catch (error) {
          console.error('Error submitting marks:', error);
        } finally {
          setIsSubmitting(false);
        }
      }
    }
  };

  // Update total marks when subjects change
  useEffect(() => {
    const total = subjects.reduce((sum, subject) => sum + subject.maxMarks, 0);
    setTotalMarks(total);
  }, [subjects]);

  const stats = calculateStats();

  if (!selectedStudent) {
    return (
      <Card>
        <CardContent className='h-full'>
          <Box className='text-center py-16'>
            <PersonIcon
              className='text-gray-300 mb-4'
              style={{ fontSize: 60 }}
            />
            <Typography variant='h6' className='text-gray-400 mb-2'>
              No Student Selected
            </Typography>
            <Typography variant='body2' className='text-gray-500'>
              Please select a student from the left panel to enter marks
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const printMarksheet = async () => {
    if (isPrinting) return;

    setIsPrinting(true);
    try {
      const res = dispatch(
        downloadStudentMarksReport({
          examType: examType,
          studentId: selectedStudent?.studentId,
        }),
      );
      if (res.status == 200) {
        const blob = await res.unwrap();
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `marksheet_${selectedStudent?.studentId}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Error in downloading the reports.');
      }
    } catch (error) {
      console.error('Error printing marksheet:', error);
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <div className='space-y-4'>
      {/* Student Information Card */}
      <Card>
        <CardContent>
          <Box className='flex items-center justify-between flex-wrap gap-2 mb-4'>
            <Box className='flex items-center gap-2'>
              <Box className='size-8 bg-teal-500 rounded-lg flex items-center justify-center'>
                <Typography className='text-white font-bold text-sm'>
                  <GoPerson className='size-5' />
                </Typography>
              </Box>
              <Typography variant='h6' className='text-teal-700 font-semibold'>
                Student Information
              </Typography>
            </Box>
            <Box className='flex gap-2 items-center'>
              <Button
                variant='outlined'
                size='small'
                onClick={() => printMarksheet()}
                startIcon={
                  isPrinting ? (
                    <Spinner size='small' />
                  ) : (
                    <IoPrintOutline size={20} />
                  )
                }
                disabled={isPrinting || !isMarksAlreadySubmitted}
              >
                {isPrinting ? 'Printing...' : 'Print Marksheet'}
              </Button>
            </Box>
          </Box>

          <Grid
            container
            spacing={4}
            className='flex items-center justify-between'
          >
            <Grid item xs={3}>
              <Typography variant='body2' className='text-gray-600 mb-1'>
                Name
              </Typography>
              <Typography variant='subtitle1' className='font-semibold'>
                {selectedStudent?.studentName}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='body2' className='text-gray-600 mb-1'>
                Student Id
              </Typography>
              <Typography variant='subtitle1' className='font-semibold'>
                {selectedStudent?.studentId}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='body2' className='text-gray-600 mb-1'>
                Department
              </Typography>
              <Typography variant='subtitle1' className='font-semibold'>
                {department}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='body2' className='text-gray-600 mb-1'>
                Class
              </Typography>
              <Typography variant='subtitle1' className='font-semibold'>
                {className}
              </Typography>
            </Grid>
            {instituteType?.toLowerCase() === 'college' ? (
              <Grid item xs={3}>
                <Typography variant='body2' className='text-gray-600 mb-1'>
                  Section
                </Typography>
                <Typography variant='subtitle1' className='font-semibold'>
                  {section}
                </Typography>
              </Grid>
            ) : null}
          </Grid>

          <Box className='mt-4 flex flex-col items-center justify-start gap-2'>
            <Typography variant='body1' className='text-gray-600 self-start'>
              Exam Type
            </Typography>
            <Box className='flex items-center self-start gap-2'>
              <Typography variant='body1' className='font-semibold'>
                {examType}
              </Typography>
              <Chip
                label={`${totalMarks || ''} Marks`}
                size='small'
                className='bg-orange-200 text-orange-800 font-bold'
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Subject-wise Marks Entry Card */}
      <Card>
        <CardContent>
          <Box className='flex items-center gap-2 mb-4'>
            <Box className='size-8 bg-purple-500 rounded-lg flex items-center justify-center'>
              <EditIcon className='text-white' fontSize='small' />
            </Box>
            <Typography variant='h6' className='text-purple-700 font-semibold'>
              Subject-wise Marks Entry
            </Typography>
          </Box>
          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{ maxHeight: '60vh', overflow: 'auto' }}
          >
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell>
                    <strong>Subject</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Max Marks</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Pass Marks</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Obtained Marks</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Grade</strong>
                  </TableCell>
                  <TableCell align='center'>
                    <strong>Absent</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjects?.map((subject) => (
                  <TableRow key={subject.id} hover>
                    <TableCell>
                      <FormLabel>{subject.name}</FormLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <FormLabel> {subject?.maxMarks}</FormLabel>
                    </TableCell>
                    <TableCell align='center'>
                      <FormLabel> {subject?.passMarks}</FormLabel>
                    </TableCell>
                    {/* Marks TextField */}
                    <TableCell align='center'>
                      <TextField
                        size='small'
                        type='number'
                        value={subject?.marks === null ? '' : subject?.marks}
                        onChange={(e) => {
                          if (e.target.value === '') {
                            handleMarksChange(subject.id, null);
                            return;
                          }
                          let val = parseFloat(e.target.value);
                          if (val < 0) val = 0;
                          if (val > subject?.maxMarks) val = subject?.maxMarks;
                          handleMarksChange(subject.id, val);
                        }}
                        onKeyDown={(e) => {
                          if (['-', '+', 'e', 'E'].includes(e.key)) {
                            e.preventDefault(); // block minus, plus, and scientific notation
                          }
                        }}
                        variant='standard'
                        sx={{
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                            {
                              display: 'none',
                            },
                          width: 80,
                        }}
                        inputProps={{
                          min: 0,
                          max: subject?.maxMarks,
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                        }}
                        disabled={subject.absent}
                      />
                    </TableCell>
                    {/* Status */}
                    <TableCell align='center' className='font-bold'>
                      {subject?.status?.toLowerCase() === 'pass' ? (
                        <div className='text-green-600 '>Pass</div>
                      ) : subject?.status?.toLowerCase() === 'fail' ? (
                        <div className='text-red-600 '>Fail</div>
                      ) : subject?.status?.toLowerCase() === 'absent' ? (
                        <div className='text-blue-500 '>Absent</div>
                      ) : (
                        (subject?.status ?? '--')
                      )}
                    </TableCell>
                    {/* Grade Chip Field */}
                    <TableCell align='center' className='font-semibold'>
                      {getGrade(subject?.marks, subject?.maxMarks)}
                    </TableCell>
                    <TableCell align='center'>
                      <Checkbox
                        checked={subject.absent}
                        onChange={(e) => {
                          handleAbsentChange(subject.id, e.target.checked);
                        }}
                        size='small'
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {subjects.length == 0 && (
                  <TableRow>
                    <TableCell colSpan='7' align='center'>
                      No Subjects Scheduled for selected Exam Type.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Action Buttons */}
          {subjects.length > 0 && (
            <Box className='flex gap-2 pt-4 justify-end'>
              <Button
                variant='contained'
                color={
                  hasChange || isMarksAlreadySubmitted ? 'error' : 'success'
                }
                startIcon={
                  isSubmitting || parentIsSubmitting ? (
                    <Spinner size='small' />
                  ) : (
                    <EditIcon />
                  )
                }
                onClick={handleSubmit}
                disabled={isSubmitting || parentIsSubmitting}
                size='large'
              >
                {isSubmitting || parentIsSubmitting
                  ? 'Submitting...'
                  : isMarksAlreadySubmitted
                    ? 'Resubmit Marks'
                    : 'Submit Marks'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card
            sx={{
              minWidth: 150,
              flex: 1,
              backgroundColor: '#2563eb',
              color: 'white',
            }}
          >
            <CardContent className='text-center py-4'>
              <Box className='flex items-center justify-center gap-2 mb-2'>
                <Box className='size-8 bg-white/20 rounded-full flex items-center justify-center'>
                  <Typography className='text-white font-bold text-sm'>
                    📊
                  </Typography>
                </Box>
              </Box>
              <Typography variant='h4' className='font-bold mb-1'>
                {stats.totalMarks}
              </Typography>
              <Typography variant='body2' className='opacity-90'>
                Total Marks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card
            sx={{
              minWidth: 150,
              flex: 1,
              backgroundColor: '#059669',
              color: 'white',
            }}
          >
            <CardContent className='text-center py-4'>
              <Box className='flex items-center justify-center gap-2 mb-2'>
                <Box className='size-8 bg-white/20 rounded-full flex items-center justify-center'>
                  <Typography className='text-white font-bold text-sm'>
                    🎯
                  </Typography>
                </Box>
              </Box>
              <Typography variant='h4' className='font-bold mb-1'>
                {stats.marks}
              </Typography>
              <Typography variant='body2' className='opacity-90'>
                Obtained Marks
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card
            sx={{
              minWidth: 150,
              flex: 1,
              backgroundColor: '#ea580c',
              color: 'white',
            }}
          >
            <CardContent className='text-center py-4'>
              <Box className='flex items-center justify-center gap-2 mb-2'>
                <Box className='size-8 bg-white/20 rounded-full flex items-center justify-center'>
                  <Typography className='text-white font-bold text-sm'>
                    %
                  </Typography>
                </Box>
              </Box>
              <Typography variant='h4' className='font-bold mb-1'>
                {stats.percentage}%
              </Typography>
              <Typography variant='body2' className='opacity-90'>
                Percentage
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card
            sx={{
              minWidth: 150,
              flex: 1,
              backgroundColor: stats.result === 'FAIL' ? '#dc2626' : '#059669',
              color: 'white',
            }}
          >
            <CardContent className='text-center py-4'>
              <Box className='flex items-center justify-center gap-2 mb-2'>
                <Box className='size-8 bg-white/20 rounded-full flex items-center justify-center'>
                  <Typography className='text-white font-bold text-sm'>
                    {stats.result === 'FAIL' ? '✗' : '✓'}
                  </Typography>
                </Box>
              </Box>
              <Typography variant='h6' className='font-bold mb-1'>
                {stats.result}
              </Typography>
              <Typography variant='body2' className='opacity-90'>
                Result
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={modalAlert}
        onClose={() => setModalAlert(false)}
        aria-describedby='alert-dialog-slide-description'
      >
        <DialogTitle className='text-red-500 font-bold text-center '>
          Alert!
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-slide-description'>
            Please enter marks for all the Subjects.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setModalAlert(false)}
            variant='contained'
            className='mx-auto'
          >
            ok
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

MarksEntryForm.propTypes = {
  selectedStudent: PropTypes.object,
  onMarksSubmit: PropTypes.func,
  examType: PropTypes.string,
  department: PropTypes.string,
  class: PropTypes.string,
  section: PropTypes.string,
  instituteType: PropTypes.string,
  isSubmitting: PropTypes.bool,
};

export default MarksEntryForm;
